import React, { useContext, useEffect, useState } from "react";
import "./index.less";
import { Progress, List } from "antd";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ZPopover from "@atoms/ZPopover";
import {
	GuideIntroductionSteps,
	UpdateCheckListBarDataPayload,
	UpdateCheckListBarDataRequest,
	CheckListModule,
	Steps,
	Stages,
	Modules
} from "../../contracts";
import {
	MainCheckListStage,
	HighlightedCheckListStage,
	ExploreCheckListStage
} from "./Stages";
import { CurrentGuideProgress, OnboardingState } from "../../store/reducers";
import {
	OnboardingActionCreators,
	useOnboardingDispatch,
	useOnboardingSelector
} from "../../store";
import {
	getIncompleteStageDetail,
	navigateToNextModule
} from "../../hooks/useInitOnboarding";
import { ParentContext } from "../../store/context";
import {
	CheckListBarMoreOptions,
	OnboardingChecklistBarProps,
	MainModulesIds
} from "./interface";
import ZModal from "@atoms/ZModal";
import EndTutorialFlash from "../../assets/EndTutorialFlash.svg";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import ZButton from "@atoms/ZButton";
import { EColors } from "@atoms/ZColors/interface";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player";
import { useHistory } from "react-router";
import { ZomentumRoutes } from "@/Contracts/common";
import lottie from "lottie-web";
import LottieRipple from "../../assets/LottieRipple.json";
import LottieConfetti from "../../assets/LottieConfetti.json";

const OnBoardingChecklistBar: React.FC<OnboardingChecklistBarProps> = ({
	parentContext,
	checkListBarData,
	moduleOrder,
	activeStep,
	quitOnboarding,
	updateActiveModuleOnClick,
	skipModule,
	updateRevisitModule,
	onboardingMaps,
	activeModule,
	hideCheckList
}) => {
	const [guideProgress, setGuideProgress] = useState<number>(0);

	const dispatch = useOnboardingDispatch();

	const active_module = checkListBarData?.active_module ?? null;
	const modules = checkListBarData?.modules;

	//Set the progress of checklist guide
	const setGuideProgressBar = () => {
		if (modules) {
			let activeGuide: Stages[] | CheckListModule[] = [],
				checkedList: boolean[] = [];
			if (active_module !== null) {
				const guideStage = modules?.[active_module]?.stages ?? [];
				activeGuide =
					onboardingMaps?.moduleStages?.[
						moduleOrder[active_module]
					] ?? [];
				checkedList = activeGuide
					.map((_, index) => {
						return guideStage[index]?.is_complete && `${index}`;
					})
					.filter(Boolean) as boolean[];
			} else {
				activeGuide = modules;
				checkedList = modules
					.map(item => {
						return item.is_complete;
					})
					.filter(Boolean);
			}

			setGuideProgress((100 * checkedList.length) / activeGuide.length);
		}
	};

	useEffect(() => {
		setGuideProgressBar();
	}, [checkListBarData, onboardingMaps]);

	const handleMainCheckListGuideChange = async (
		moduleId: CheckboxValueType[],
		areAllModuleCompleted: boolean
	) => {
		const mainModule = checkListBarData?.modules ?? [];
		let activeModuleIndex = 0;
		if (areAllModuleCompleted) {
			const moduleToRevisit = MainModulesIds.filter(id => {
				return !moduleId.includes(id);
			})
				.toString()
				.split("_")
				.pop();

			activeModuleIndex = Number(moduleToRevisit);
			await updateRevisitModule(activeModuleIndex);
		} else {
			activeModuleIndex = mainModule.findIndex(stage => {
				return !stage.is_complete;
			});
		}

		const { activeModule, activeStage, activeStep } =
			await getIncompleteStageDetail(activeModuleIndex);

		const nextDetails: CurrentGuideProgress = {
			activeModule,
			activeStage,
			activeStep
		};

		if (nextDetails.activeModule) {
			const moduleIndex = moduleOrder.indexOf(nextDetails.activeModule);
			await updateActiveModuleOnClick(moduleIndex, nextDetails);
			navigateToNextModule(nextDetails, parentContext);
			if (
				[Modules.DOCUMENT, Modules.OPPORTUNITY].includes(
					nextDetails.activeModule
				)
			) {
				await dispatch(OnboardingActionCreators.getDummyClientData());
			}
			if (nextDetails.activeModule === Modules.DOCUMENT) {
				await dispatch(
					OnboardingActionCreators.getDocumentTemplateData()
				);
			}
		}
	};

	const handleMoreIconOptionsClick = async (
		option: CheckListBarMoreOptions
	) => {
		switch (option) {
			case CheckListBarMoreOptions.HELP_CENTRE: {
				window.open("https://help.zomentum.com/support/home");
				break;
			}

			case CheckListBarMoreOptions.QUIT_TUTORIAL: {
				await quitOnboarding();
				break;
			}

			case CheckListBarMoreOptions.SKIP_MODULE: {
				await skipModule();
				break;
			}

			default:
				return;
		}
	};

	const showSkip =
		!checkListBarData?.is_checklist_complete &&
		(active_module !== null ||
			!!checkListBarData?.modules?.some(module => !module.is_complete));

	const listData = [
		{
			label: "Help Center",
			disabled: false,
			className: "mb-2",
			onclick: () =>
				handleMoreIconOptionsClick(CheckListBarMoreOptions.HELP_CENTRE)
		},
		...(showSkip
			? [
					{
						label: "Skip Module",
						className: "mb-2",
						disabled: false,
						onclick: () =>
							handleMoreIconOptionsClick(
								CheckListBarMoreOptions.SKIP_MODULE
							)
					}
			  ]
			: []),
		{
			label: checkListBarData?.is_checklist_complete
				? "Quit Walkthrough"
				: "Pause Walkthrough",
			disabled: false,
			onclick: () =>
				handleMoreIconOptionsClick(
					CheckListBarMoreOptions.QUIT_TUTORIAL
				)
		}
	];

	const nextModule: number =
		checkListBarData?.modules?.findIndex(stage => !stage.is_complete) ?? -1;

	useEffect(() => {
		if (nextModule > -1 && active_module === null) {
			lottie.loadAnimation({
				container: document.querySelector(".next-upcoming-module")
					?.firstChild?.childNodes[1] as Element,
				animationData: LottieRipple,
				name: "UpcomingModuleRipple"
			});
		}
		return () => lottie.destroy("UpcomingModuleRipple");
	}, [nextModule, active_module]);

	const showHighlightedCheckList =
		activeStep !== null &&
		GuideIntroductionSteps.includes(activeStep) &&
		!checkListBarData?.is_checklist_complete;

	return (
		<div
			className={`checklist-bar-container bottom-4 
			${
				activeModule && activeStep
					? `${activeModule?.toLowerCase()}_${activeStep}_container`
					: ""
			} ${hideCheckList ? "hidden" : ""}`}
		>
			{active_module !== null &&
				activeStep !== null &&
				!showHighlightedCheckList && (
					<div className="h-0">
						<Progress
							percent={guideProgress}
							size="small"
							showInfo={false}
							strokeColor="#B7EB8F"
							className="checklist-progress"
						/>
					</div>
				)}
			<div className="checkbox-bar-container">
				<div className="checkbox-bar decoration-white h-16">
					{active_module === null ? (
						<MainCheckListStage
							moduleOrder={moduleOrder}
							handleMainCheckListGuideChange={
								handleMainCheckListGuideChange
							}
							checkListBarData={checkListBarData}
						/>
					) : active_module !== null && showHighlightedCheckList ? (
						<HighlightedCheckListStage
							checkListBarData={checkListBarData}
							moduleOrder={moduleOrder}
							moduleStageMap={onboardingMaps?.moduleStages}
						/>
					) : (
						<ExploreCheckListStage
							checkListBarData={checkListBarData}
							moduleOrder={moduleOrder}
							moduleStageMap={onboardingMaps?.moduleStages}
						/>
					)}
				</div>
				<div className="more-item cursor-pointer ">
					<ZPopover
						placement="top"
						content={
							<List
								itemLayout="horizontal"
								dataSource={listData}
								renderItem={item => (
									<List.Item
										className={`more-icon-list-item ${
											item.disabled
												? "pointer-events-none"
												: "cursor-pointer"
										} ${item.className}`}
										onClick={item.onclick}
									>
										{item.label}
									</List.Item>
								)}
							/>
						}
						trigger="hover"
						size="sm"
						overlayClassName="onboarding-more-actions"
					>
						<ZIcon
							name={EIcons.MOREVERTICAL}
							size="md"
							colorVariant={1}
							fill={true}
						/>
					</ZPopover>
				</div>
			</div>
		</div>
	);
};

/**
 * This hook will hide and show checklist bar
 * @param {isCheckListActive} - Flag to show and hide bar
 * @return void
 */
export const useCheckListGuide = (isCheckListActive: boolean) => {
	const parentContext = useContext(ParentContext);
	const {
		activeStep,
		checkListBarData,
		moduleOrder,
		activeModule,
		onboardingMaps,
		hideCheckList
	} = useOnboardingSelector((state: OnboardingState) => {
		const { activeModule, activeStage, activeStep } =
			state.currentGuideProgress;
		const isWelcomeVideo = activeStep === Steps.WELCOME_VIDEO;
		const isAddOpportunityStep =
			activeModule === Modules.OPPORTUNITY &&
			activeStage === Stages.ADD_OPPORTUNITY &&
			!!activeStep &&
			[
				Steps.HIGHLIGHT_ADD_OPPORTUNITY_DRAWER,
				Steps.HIGHLIGHT_OPPORTUNITY_SHARE_WITH_VENDOR_CHECKBOX,
				Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON
			].includes(activeStep);
		const hideCheckList =
			!state.isProgressInitialized ||
			isAddOpportunityStep ||
			isWelcomeVideo;
		return {
			activeModule,
			activeStep,
			checkListBarData: state.checkListBarData,
			moduleOrder: state.moduleOrder,
			onboardingMaps: state.onboardingMaps,
			hideCheckList
		};
	});

	const dispatch = useOnboardingDispatch();
	const history = useHistory();

	const [tutorialComplete, setTutorialComplete] = useState(false);
	const [visibleLottiePlayer, setVisibleLottiePlayer] = useState(false);

	const markCheckListComplete = async () => {
		try {
			const requestPayload: UpdateCheckListBarDataPayload = {
				is_checklist_active: false,
				is_checklist_complete: true
			};

			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			dispatch(
				OnboardingActionCreators.updateActiveProgressData({
					activeModule: null,
					activeStage: null,
					activeStep: null
				})
			);
			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
			setTutorialComplete(false);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const areAllModulesCompleted = checkListBarData?.modules?.every(
			eachModule => eachModule.is_complete
		);
		if (
			areAllModulesCompleted &&
			!checkListBarData?.is_checklist_complete
		) {
			setVisibleLottiePlayer(true);
		}
	}, [checkListBarData]);

	const skipModule = async () => {
		try {
			const currentModules = checkListBarData?.modules;
			let activeModuleIndex: number | null = null;
			let currentModule: CheckListModule | undefined;

			// If module is ongoing
			if (activeModule) {
				activeModuleIndex = moduleOrder.findIndex(
					eachModule => eachModule === activeModule
				);
				currentModule = currentModules?.[activeModuleIndex];
			} else {
				// Get the immediate incomplete module
				activeModuleIndex = (currentModules ?? []).findIndex(
					eachModule => {
						return !eachModule.is_complete;
					}
				);
				currentModule = currentModules?.[activeModuleIndex];
			}

			if (!currentModule) {
				return;
			}
			currentModule.is_complete = true;
			currentModule.stages.forEach(stage => (stage.is_complete = true));

			const requestPayload: UpdateCheckListBarDataPayload = {
				active_module: null,
				modules: currentModules
			};
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			dispatch(
				OnboardingActionCreators.updateActiveProgressData({
					activeModule: null,
					activeStage: null,
					activeStep: null
				})
			);
			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
		} catch (error) {
			console.error(error);
		}
	};

	const updateActiveModuleOnClick = async (
		activeModuleIndex: number,
		nextSteps: CurrentGuideProgress
	) => {
		try {
			const requestPayload: UpdateCheckListBarDataPayload = {
				active_module: activeModuleIndex
			};
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			dispatch(
				OnboardingActionCreators.updateActiveProgressData(nextSteps)
			);
			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
		} catch (error) {
			console.error(error);
		}
	};

	const quitOnboarding = async () => {
		try {
			const requestPayload: UpdateCheckListBarDataPayload = {
				is_checklist_active: false
			};
			const activeModuleIndex = checkListBarData?.active_module ?? null;
			const allModules = checkListBarData?.modules;
			const activeModule =
				activeModuleIndex !== null
					? allModules?.[activeModuleIndex] ?? null
					: null;
			if (
				checkListBarData?.is_checklist_complete &&
				activeModuleIndex !== null
			) {
				if (activeModule) {
					activeModule.is_complete = true;
					activeModule.stages.forEach(
						stage => (stage.is_complete = true)
					);
				}
				requestPayload.active_module = null;
				requestPayload.modules = allModules;
			}
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			dispatch(
				OnboardingActionCreators.updateActiveProgressData({
					activeModule: null,
					activeStage: null,
					activeStep: null
				})
			);

			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);

			history.replace(ZomentumRoutes.OpportunityList);
		} catch (error) {
			console.error(error);
		}
	};

	const handleQuitTutorial = async () => {
		ZModal.confirm({
			title: checkListBarData?.is_checklist_complete
				? "Are You Sure You Want To Quit?"
				: "Are You Sure You Want To Pause?",
			zIndex: 930,
			icon: false,
			content: checkListBarData?.is_checklist_complete ? null : (
				<>
					<ZTypography.ZText
						level={1}
						color={EColors.GRAY}
						colorVariant={9}
						className="mt-2 block"
					>
						Your progress will be saved, you can comeback and resume
						the walkthrough anytime.
					</ZTypography.ZText>
				</>
			),
			cancelText: "Cancel",
			okText: checkListBarData?.is_checklist_complete
				? "Quit Walkthrough"
				: "Save progress",
			onOk: async () => {
				await quitOnboarding();
			},
			closable: true
		});
	};

	const updateRevisitModule = async (revisitModuleIndex: number) => {
		try {
			const allModules = checkListBarData?.modules;
			if (allModules) {
				allModules[revisitModuleIndex].is_complete = false;
				allModules[revisitModuleIndex].stages.forEach(
					stage => (stage.is_complete = false)
				);
			}

			const requestPayload: UpdateCheckListBarDataPayload = {
				modules: allModules
			};

			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
		} catch (error) {
			console.error(error);
		}
	};

	const onFlashComplete = () => {
		setTutorialComplete(true);
		setVisibleLottiePlayer(false);
	};

	const onPlayerEvent = (event: PlayerEvent) => {
		if (event === PlayerEvent.Complete) onFlashComplete();
	};

	return {
		OnboardingCheckList: isCheckListActive ? (
			<>
				<OnBoardingChecklistBar
					activeStep={activeStep}
					checkListBarData={checkListBarData}
					parentContext={parentContext}
					moduleOrder={moduleOrder}
					quitOnboarding={handleQuitTutorial}
					updateActiveModuleOnClick={updateActiveModuleOnClick}
					skipModule={skipModule}
					updateRevisitModule={updateRevisitModule}
					onboardingMaps={onboardingMaps}
					activeModule={activeModule}
					hideCheckList={hideCheckList}
				/>
				<ZModal
					centered
					visible={tutorialComplete}
					size="sm"
					closable={false}
					footer={null}
					className="end-tutorial rounded-lg"
					zIndex={930}
				>
					<div className="flex flex-col">
						<div className="illustration">
							<EndTutorialFlash />
						</div>
						<div className="end-tutorial-content flex flex-col">
							<ZTypography.ZText
								level={1}
								weight={EFontWeights.MEDIUM}
							>
								Congratulations!
							</ZTypography.ZText>
							<ZTypography.ZText level={1}>
								You've successfully completed the walkthrough.
							</ZTypography.ZText>
							<ZTypography.ZText level={1}>
								Get started with adding your own opportunities,
								documents and much more.
							</ZTypography.ZText>
							<div className="flex items-start mt-2">
								<ZIcon
									className="mx-0"
									name={EIcons.WARNINGCIRCLE}
									color={EColors.BLUE}
									colorVariant={6}
								/>
								<ZTypography.ZText
									className="ml-1"
									colorVariant={7}
									level={1}
								>
									The data generated during the walkthrough is
									only for demonstration purposes and can be
									deleted.
								</ZTypography.ZText>
							</div>
						</div>
						<div className="end-tutorial-cta mt-4 flex justify-end">
							<ZButton
								type="primary"
								className="rounded"
								onClick={markCheckListComplete}
							>
								End Walkthrough
							</ZButton>
						</div>
					</div>
				</ZModal>
				{visibleLottiePlayer ? (
					<Player
						className="end-tutorial-player"
						src={LottieConfetti}
						autoplay
						loop={false}
						onEvent={onPlayerEvent}
					/>
				) : null}
			</>
		) : null
	};
};
